import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import {
	applyBackInStockToCache,
	BACK_IN_STOCK_STALE_MS,
	backInStockQueryKey,
	isBackInStockRequested,
} from '../lib/backInStock';
import {
	CATALOG_FACETED_QUERY_KEY,
	CATALOG_ITEM_QUERY_KEY,
} from '../lib/catalogQueryKeys';
import * as catalogService from '../services/portalCatalogService';
import { useBackInStockTextPromptStore } from '../stores/backInStockTextPromptStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

export interface UseBackInStockRequestResult {
	/**
	 * Whether "Notify me" can be offered at all: a signed-in customer with an
	 * account selected, and a server that answered the list of requests. False
	 * while that list is loading, and stays false when it could not be loaded
	 * (for example an API that does not have the feature yet), so the control
	 * is simply not shown rather than shown broken.
	 */
	isAvailable: boolean;
	/** The customer has asked to be notified about this item. */
	isRequested: boolean;
	isSaving: boolean;
	/**
	 * Ask to be notified. Resolves with null when it was saved, or with the
	 * reason it was not (the server's message when it gave one).
	 */
	request: () => Promise<string | null>;
	/** Cancel the request. Resolves false when it could not be saved. */
	cancel: () => Promise<boolean>;
}

/** Shown when a save failed and the server gave no reason of its own. */
const GENERIC_FAILURE = 'Please try again.';

/**
 * PD-14306 — one item's "Notify me" state for the signed-in customer.
 *
 * The list of requested items is loaded once per customer and account and
 * shared through the query cache, so a page of out-of-stock cards makes one
 * request. A click shows at once and is put back if the save fails.
 */
export function useBackInStockRequest(
	itemId: number,
): UseBackInStockRequestResult {
	const queryClient = useQueryClient();
	const token = usePortalAuthStore((s) => s.token);
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const activeClientId = usePortalAuthStore((s) => s.activeClientId);
	const publicUserId = usePortalAuthStore((s) => s.profile?.id ?? null);
	const openTextPrompt = useBackInStockTextPromptStore((s) => s.open);
	const [isSaving, setIsSaving] = useState(false);
	// The disabled state only lands on the next render, so a fast double click
	// could otherwise start a second request before it does.
	const savingRef = useRef(false);

	const canAsk =
		isAuthenticated &&
		token != null &&
		publicUserId != null &&
		activeClientId != null;
	const { data, isSuccess } = useQuery({
		queryKey: backInStockQueryKey(publicUserId, activeClientId),
		queryFn: () => catalogService.getBackInStockSubscriptions(token ?? ''),
		enabled: canAsk,
		staleTime: BACK_IN_STOCK_STALE_MS,
		// A server without the feature answers 404 every time; asking again
		// would only delay hiding the control.
		retry: false,
	});

	const save = useCallback(
		async (isRequested: boolean): Promise<string | null> => {
			if (
				savingRef.current ||
				token == null ||
				publicUserId == null ||
				activeClientId == null
			) {
				return GENERIC_FAILURE;
			}

			savingRef.current = true;
			setIsSaving(true);
			applyBackInStockToCache(
				queryClient,
				publicUserId,
				activeClientId,
				itemId,
				isRequested,
			);

			try {
				if (isRequested) {
					const response = await catalogService.subscribeBackInStock(
						token,
						itemId,
					);

					if (response.textPrompt != null) {
						openTextPrompt(response.textPrompt);
					}
				} else {
					await catalogService.unsubscribeBackInStock(token, itemId);
				}

				return null;
			} catch (error) {
				applyBackInStockToCache(
					queryClient,
					publicUserId,
					activeClientId,
					itemId,
					!isRequested,
				);

				if (isRequested) {
					// The usual reason a request is refused is that the item is back
					// in stock already. Refetch what the page shows for it, so the
					// card offers Add to Cart instead of a request that cannot be made.
					void queryClient.invalidateQueries({
						queryKey: CATALOG_FACETED_QUERY_KEY,
					});
					void queryClient.invalidateQueries({
						queryKey: [...CATALOG_ITEM_QUERY_KEY, itemId],
					});
				}

				return error instanceof Error && error.message !== ''
					? error.message
					: GENERIC_FAILURE;
			} finally {
				savingRef.current = false;
				setIsSaving(false);
			}
		},
		[token, publicUserId, activeClientId, queryClient, itemId, openTextPrompt],
	);

	const request = useCallback(() => save(true), [save]);
	const cancel = useCallback(
		async () => (await save(false)) == null,
		[save],
	);

	return {
		isAvailable: canAsk && isSuccess,
		isRequested: isBackInStockRequested(data, itemId),
		isSaving,
		request,
		cancel,
	};
}
